/**
 * src/state/riskWriteOutbox.js
 *
 * Module-singleton pending-write queue ("outbox") for the canonical per-record
 * risk writes (risks-write). It exists so a risk edit is durable ONLY once its
 * canonical write is acknowledged — closing the silent-divergence gap ratified
 * in docs/decisions/0001-risks-documents-source-of-truth.md (ADR-0001 §3).
 *
 * Contract (ADR-0001 §3.1/§3.2):
 *   • de-dupes by record id — the latest queued write for an id wins, so
 *     repeated edits collapse to the newest value;
 *   • drives a bounded exponential-backoff auto-retry loop;
 *   • flags the record as failed (via the caller's onStatus callback) on the
 *     first failure so the edit stays visible but marked, and clears it on ack;
 *   • terminal 4xx (non-409) failures stop retrying and surface a per-record
 *     error, and are removed from the depth so background refresh resumes;
 *   • a 409 on an EDIT (isCreate: false) is retried like any other transient
 *     failure — the server re-reads its own row version each call, so a
 *     re-send re-applies safely (ID-GEN-003) — EXCEPT a 409 that names the
 *     fields that clashed (`err.conflicts`, GH #7935): someone else changed
 *     the same field, a re-send would clash identically, so it is terminal
 *     and surfaced, never retried or dropped;
 *   • one write per record is in flight at a time (GH #7935). A newer edit
 *     queued while one is in flight waits for it, so the newer one is sent
 *     measured from what the earlier one actually stored (onCommitted, which
 *     fires even for a write a newer edit has superseded);
 *   • a 409 on a CREATE (isCreate: true) is NEVER blindly resent — the id it
 *     collided with permanently belongs to a different record, so a re-send
 *     would fail identically forever. Instead the caller's onCreateConflict
 *     hook decides: reconcile as success (the earlier attempt already
 *     landed server-side and this is a retry of a lost response), or
 *     re-issue the create so the server mints a fresh id (ID-GEN-003);
 *   • replays every still-queued write when connectivity returns ('online');
 *   • exposes queue depth + a pending-check so the UI can show "N unsaved" and
 *     the refresh loops can avoid overlaying a stale server row over a
 *     failed-but-visible optimistic edit.
 *
 * It is a singleton (same idiom as engineEventBus.js) so the two independent
 * risk refresh loops — usePersistence's background poll and Risks.jsx's 60 s
 * refetch — can both consult hasPendingRiskWrites() without prop-drilling.
 *
 * Purely additive and reversible: removing this module reverts risk writes to
 * the previous fire-and-forget behaviour with no data-shape change (ADR §3.2).
 *
 * This is the reference implementation for ID-GEN-003 (create-vs-edit 409
 * handling) — any future per-record store's client outbox (vendors,
 * suppliers, as ADR-0002/RC2-RC4 land) should copy this pattern rather than
 * the old blind-retry-on-any-409 version.
 */

// Exponential backoff schedule (ms). Index by attempt count. The final value is
// the ceiling for any further scheduled retries before auto-retry gives up and
// waits for reconnect / a manual retry.
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];
// After this many failed attempts, stop the automatic timer loop and leave the
// entry flagged as 'failed', awaiting a reconnect ('online') or a manual retry.
const MAX_AUTO_ATTEMPTS = 6;

// id -> entry. entry = { id, writeFn, onSuccess, onStatus, isCreate,
//   onCreateConflict, attempts, reissues, timer, terminal }
const entries = new Map();
// id -> the token of the write currently on the wire for that record.
const inflight = new Map();
const subscribers = new Set();
let onlineBound = false;

function bindOnline() {
  if (onlineBound || typeof window === 'undefined' || !window.addEventListener) return;
  onlineBound = true;
  window.addEventListener('online', () => replayAll());
}

function notify() {
  const snapshot = summary();
  for (const fn of subscribers) {
    try {
      fn(snapshot);
    } catch (e) {
      console.warn('[riskWriteOutbox] subscriber error:', e);
    }
  }
}

function clearTimer(entry) {
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
}

// 409 (write conflict) → retry: the server re-reads its own row version each
// call, so a re-send re-applies safely. Network / no-status / 5xx → retry.
// Any other 4xx → terminal (a re-send would fail identically).
function isRetryable(err) {
  const s = err?.httpStatus;
  if (s == null) return true;
  if (s === 409) return !(Array.isArray(err.conflicts) && err.conflicts.length > 0);
  if (s >= 400 && s < 500) return false;
  return true;
}

// True while `entry` is still the live queue member for its id. A queued write
// can be superseded (dedupe replaced it) or dropped (discard / reset) while its
// promise is still in flight; a stale continuation must then be a no-op so it
// cannot re-schedule a timer or fire callbacks against a record the user has
// moved on from.
function isCurrent(entry) {
  return entries.get(entry.id) === entry;
}

// Resolve a 409 on a CREATE entry via the caller's onCreateConflict hook
// instead of the generic retry loop. Returns true if it fully handled the
// outcome (success adopted, or a re-issue scheduled) — false means "fall
// through to the normal failure handling" (reconciliation declined, threw,
// or returned nothing usable).
async function resolveCreateConflict(entry, err) {
  let outcome;
  try {
    outcome = await entry.onCreateConflict(err);
  } catch (reconcileErr) {
    console.error('[riskWriteOutbox] onCreateConflict handler error:', reconcileErr);
    return false;
  }
  if (!isCurrent(entry)) return true; // superseded while reconciling — no further action

  if (outcome?.action === 'adopt' && outcome.serverRecord) {
    // The earlier attempt already landed server-side; this was a retry of a
    // request whose response was lost. Treat it as the success it is.
    entries.delete(entry.id);
    notify();
    try {
      entry.onSuccess?.(outcome.serverRecord);
    } catch (e) {
      console.error('[riskWriteOutbox] onSuccess handler error:', e);
    }
    return true;
  }

  if (
    outcome?.action === 'reissue' &&
    typeof outcome.writeFn === 'function' &&
    entry.reissues < MAX_AUTO_ATTEMPTS
  ) {
    // Genuine two-different-records-same-id collision — don't resend the
    // doomed payload. Swap in the caller's re-issued create (server mints a
    // fresh id) and retry immediately, no backoff: this is a deliberate
    // corrective action, not a transient failure. Reset attempts so a
    // create-conflict doesn't eat into the same budget as ordinary retries —
    // but cap the number of reissues separately (entry.reissues), since
    // attempts resetting to 0 every time would otherwise let a persistently
    // conflicting server response loop forever without ever reaching
    // MAX_AUTO_ATTEMPTS.
    entry.writeFn = outcome.writeFn;
    entry.attempts = 0;
    entry.reissues += 1;
    run(entry);
    return true;
  }

  return false;
}

function committed(entry, serverRecord) {
  try {
    entry.onCommitted?.(serverRecord);
  } catch (e) {
    console.error('[riskWriteOutbox] onCommitted handler error:', e);
  }
}

// The write behind `token` has settled: if a newer edit of the same record
// was waiting for it, send that one now.
function release(id, token) {
  if (inflight.get(id) !== token) return;
  inflight.delete(id);
  const next = entries.get(id);
  if (next && next.waiting) {
    next.waiting = false;
    run(next);
  }
}

// Start `entry` now, or — while another write of the same record is on the
// wire — mark it to start when that one settles.
function start(entry) {
  if (inflight.has(entry.id)) {
    clearTimer(entry);
    entry.waiting = true;
    return;
  }
  run(entry);
}

function run(entry) {
  clearTimer(entry);
  const token = {};
  inflight.set(entry.id, token);
  Promise.resolve()
    .then(() => entry.writeFn())
    .then((serverRecord) => {
      // Stored, whether or not a newer edit has superseded it since.
      committed(entry, serverRecord);
      if (!isCurrent(entry)) return; // superseded / discarded mid-flight
      // Acknowledged. Remove from the queue BEFORE the success callback so
      // hasPendingRiskWrites() is already false when the caller adopts the
      // server record and lets refresh resume.
      entries.delete(entry.id);
      notify();
      try {
        entry.onSuccess?.(serverRecord);
      } catch (e) {
        console.error('[riskWriteOutbox] onSuccess handler error:', e);
      }
    })
    .catch(async (err) => {
      if (!isCurrent(entry)) return; // superseded / discarded mid-flight

      // ID-GEN-003: a 409 on a CREATE is never blindly resent — see the
      // module doc comment. Only ever engaged for entries the caller
      // explicitly flagged isCreate and supplied a reconciliation hook for;
      // anything else (edits, or a create without the hook) falls straight
      // through to the unchanged generic retry logic below.
      if (err?.httpStatus === 409 && entry.isCreate && entry.onCreateConflict) {
        const handled = await resolveCreateConflict(entry, err);
        if (handled) return;
        if (!isCurrent(entry)) return; // superseded during reconciliation
      }

      entry.attempts += 1;
      const terminal = !isRetryable(err);
      // Flag the record as failed on every failure so the edit stays visible
      // but marked (ADR §3.1 step 3).
      entry.terminal = terminal;
      try {
        entry.onStatus?.('failed', { terminal, error: err });
      } catch (e) {
        console.error('[riskWriteOutbox] onStatus handler error:', e);
      }
      if (terminal || entry.attempts >= MAX_AUTO_ATTEMPTS) {
        // Stop the auto-retry timer. Depth still counts this entry (a manual
        // retry / reconnect can revive a non-terminal one); a terminal entry is
        // dropped from depth so background refresh is not blocked forever
        // (ADR §4.6 risk 4).
        if (terminal) {
          entries.delete(entry.id);
          notify();
        } else {
          notify();
        }
        return;
      }
      const delay = BACKOFF_MS[Math.min(entry.attempts - 1, BACKOFF_MS.length - 1)];
      entry.timer = setTimeout(() => start(entry), delay);
      notify();
    })
    .finally(() => release(entry.id, token));
}

/**
 * enqueueRiskWrite(id, writeFn, { onSuccess, onStatus, isCreate, onCreateConflict })
 *
 * Queue (or replace) the canonical write for record `id`. de-dupes by id: an
 * existing entry's timer is cancelled and its writeFn/callbacks are replaced by
 * the latest ones, then the write is attempted immediately.
 *
 *   writeFn   — () => Promise<serverRecord>. Should perform the awaited
 *               canonical write (e.g. apiUpdateRisk) and resolve with the
 *               server's returned record. May reject with err.httpStatus set.
 *   onSuccess — (serverRecord) => void. Called once, on acknowledgement.
 *   onStatus  — (status, meta) => void. status is 'failed' on a failed attempt.
 *               meta = { terminal:boolean, error }.
 *   onCommitted — (serverRecord) => void. Called when THIS write was stored,
 *               even if a newer edit of the record has superseded it since —
 *               so the caller can measure the newer edit from what is stored.
 *   isCreate  — true when this write has no prior known server version (a
 *               create, not an edit of an already-persisted record). Only
 *               changes behaviour together with onCreateConflict — see below.
 *   onCreateConflict — (err) => Promise<
 *               { action: 'adopt', serverRecord } |
 *               { action: 'reissue', writeFn } |
 *               anything else (falls through to the normal failure path)>.
 *               Called instead of a blind resend on a 409 for an isCreate
 *               entry (ID-GEN-003) — see the module doc comment. Ignored for
 *               edits and for non-409 failures, which always use the
 *               unchanged generic retry/backoff path.
 */
export function enqueueRiskWrite(
  id,
  writeFn,
  { onSuccess, onStatus, onCommitted, isCreate, onCreateConflict } = {}
) {
  bindOnline();
  const key = String(id);
  const existing = entries.get(key);
  if (existing) clearTimer(existing);
  const entry = {
    id: key,
    writeFn,
    onSuccess,
    onStatus,
    onCommitted,
    isCreate: !!isCreate,
    onCreateConflict,
    attempts: existing ? 0 : 0, // a fresh edit resets the backoff (latest wins)
    reissues: 0, // separate cap on onCreateConflict 'reissue' outcomes — see resolveCreateConflict
    timer: null,
    terminal: false,
    waiting: false,
  };
  entries.set(key, entry);
  notify();
  start(entry);
}

/** Manually re-arm a queued write (from a per-record "Retry" affordance). */
export function retryRiskWrite(id) {
  const entry = entries.get(String(id));
  if (!entry) return;
  entry.attempts = 0;
  entry.terminal = false;
  start(entry);
}

/**
 * Drop a queued write without sending it (from a per-record "Discard"
 * affordance). The caller is responsible for reverting the optimistic record to
 * the server value (e.g. by re-reading the risk).
 */
export function discardRiskWrite(id) {
  const entry = entries.get(String(id));
  if (!entry) return;
  clearTimer(entry);
  entries.delete(String(id));
  notify();
}

/** Re-attempt every still-queued write immediately (reconnect replay). */
export function replayAll() {
  for (const entry of entries.values()) {
    entry.attempts = 0;
    entry.terminal = false;
    start(entry);
  }
}

/** True while any canonical risk write is queued (pending or awaiting retry). */
export function hasPendingRiskWrites() {
  return entries.size > 0;
}

/** The set of record ids with a queued write. */
export function getPendingRiskIds() {
  return new Set(entries.keys());
}

/** { depth, ids } snapshot for indicators. */
export function summary() {
  return { depth: entries.size, ids: [...entries.keys()] };
}

/** Subscribe to depth/status changes. Returns an unsubscribe fn. */
export function subscribeRiskOutbox(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/**
 * Clear the entire queue (e.g. on tenant switch / unmount). Cancels timers and
 * drops all entries WITHOUT sending them — the new tenant's state is loaded
 * fresh, so stale queued writes must not fire against it.
 */
export function resetRiskOutbox() {
  for (const entry of entries.values()) clearTimer(entry);
  entries.clear();
  // A write still on the wire for the old tenant must not hold back the new
  // tenant's first edit of a record with the same id.
  inflight.clear();
  notify();
}
